import { useCallback, useMemo, useRef, useState } from 'react';
import { Form, message } from 'antd';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useCanCheck } from '@/ability/helpers';
import {
    PROJECT_CLIENT_TICKETS_VIEW,
    PROJECT_CLIENT_TICKETS_CREATE,
    PROJECT_CLIENT_TICKETS_UPDATE,
    PROJECT_CLIENT_TICKETS_SUBMIT,
    PROJECT_CLIENT_TICKETS_SUBMIT_TO_CLIENT,
    PROJECT_CLIENT_TICKETS_ACKNOWLEDGE,
} from '@/ability';
import { contentToApiMentions, resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';
import { buildTaskDescription } from '@hooks/projects/projectHelpers';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import { validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';
import {
    acknowledgeProjectClientTicket,
    createProjectClientTicket,
    getProjectClientTickets,
    submitProjectClientTicket,
    submitProjectClientTicketToClient,
    updateProjectClientTicket,
} from '@services/projects.service';
import { invalidateProjectQueries } from '@hooks/projects/projectQueryInvalidation';

export const PROJECT_CLIENT_TICKETS_QUERY_KEY = (projectId, params) => [
    'projects',
    projectId,
    'client-tickets',
    params,
];

export const CLIENT_TICKET_COL = {
    ACTIVE: 'active',
    SUBMITTED: 'submitted',
    SUBMITTED_TO_CLIENT: 'submitted_to_client',
    ACKNOWLEDGED: 'acknowledged',
};

const MENTION_USERS_LIMIT = 100;

function getClientTicketId(item) {
    return item?.clientTicketId ?? item?.client_ticket_id ?? item?.id ?? null;
}

function normalizeClientTicketStatus(rawStatus) {
    const s = String(rawStatus ?? '').trim().toLowerCase();
    if (['acknowledged', 'resolved', 'closed', 'done', 'completed'].includes(s)) return CLIENT_TICKET_COL.ACKNOWLEDGED;
    if (['submitted_to_client', 'submitted-to-client', 'submitted to client'].includes(s)) {
        return CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT;
    }
    if (['submitted', 'submit', 'in_review', 'review'].includes(s)) return CLIENT_TICKET_COL.SUBMITTED;
    return CLIENT_TICKET_COL.ACTIVE;
}

function partitionClientTicketRows(list) {
    const active = [];
    const submitted = [];
    const submittedToClient = [];
    const acknowledged = [];
    for (const item of Array.isArray(list) ? list : []) {
        const col = normalizeClientTicketStatus(item?.status);
        if (col === CLIENT_TICKET_COL.ACKNOWLEDGED) acknowledged.push(item);
        else if (col === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT) submittedToClient.push(item);
        else if (col === CLIENT_TICKET_COL.SUBMITTED) submitted.push(item);
        else active.push(item);
    }
    return { active, submitted, submittedToClient, acknowledged };
}

function buildCreateClientTicketPayload(values, files, links) {
    const descriptionHtml = contentToApiMentions(values?.description ?? '');
    const description = buildTaskDescription(descriptionHtml);
    if (!description?.markdown) return null;
    const title = String(values?.title ?? '').trim();
    if (!title) return null;
    const payload = {
        title,
        type: 'issue',
        description: {
            markdown: description.markdown,
            ...(Array.isArray(links) && links.length > 0
                ? {
                    links: links.map(({ url, title: linkTitle }) => ({
                        type: 'link',
                        url,
                        title: linkTitle || url,
                    })),
                }
                : {}),
        },
    };
    if (Array.isArray(files) && files.length > 0) {
        const formData = new FormData();
        formData.append('title', payload.title);
        formData.append('type', payload.type);
        formData.append('description', JSON.stringify(payload.description));
        if (Array.isArray(links) && links.length > 0) {
            formData.append(
                'attachments',
                JSON.stringify(
                    links.map(({ url, title: linkTitle }) => ({
                        type: 'link',
                        url,
                        title: linkTitle || url,
                    })),
                ),
            );
        }
        files.forEach((file) => formData.append('files', file));
        return formData;
    }
    if (Array.isArray(links) && links.length > 0) {
        payload.attachments = links.map(({ url, title: linkTitle }) => ({
            type: 'link',
            url,
            title: linkTitle || url,
        }));
    }
    return payload;
}

export function useProjectClientTickets(projectId, params = { page: 1 }, options = {}) {
    const can = useCanCheck();
    const queryClient = useQueryClient();
    const mergeSubmittedToClientIntoSubmitted = Boolean(options?.mergeSubmittedToClientIntoSubmitted);
    const mergeSubmittedIntoActive = Boolean(options?.mergeSubmittedIntoActive);
    const canView = can(PROJECT_CLIENT_TICKETS_VIEW);
    const canCreate = can(PROJECT_CLIENT_TICKETS_CREATE);
    const canUpdate = can(PROJECT_CLIENT_TICKETS_UPDATE);
    const canSubmit = can(PROJECT_CLIENT_TICKETS_SUBMIT);
    const canSubmitToClient = can(PROJECT_CLIENT_TICKETS_SUBMIT_TO_CLIENT);
    const canAcknowledge = can(PROJECT_CLIENT_TICKETS_ACKNOWLEDGE);
    const pageParam = params?.page ?? 1;

    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId && canView });
    const isClientTicketsEnabled = project?.capabilities?.useTasks !== false && project?.capabilities?.clientSubmissionForApproval !== false;
    const mentionUsers = useMemo(
        () => (project?.assignedUsers ?? []).slice(0, MENTION_USERS_LIMIT),
        [project?.assignedUsers],
    );

    const query = useQuery({
        queryKey: PROJECT_CLIENT_TICKETS_QUERY_KEY(projectId, { page: pageParam }),
        queryFn: async () => {
            if (pageParam === 1) {
                const res = await getProjectClientTickets(projectId, { page: 1, limit: 100 });
                return { items: res?.items ?? [], total: res?.total ?? 0 };
            }
            
            const prevData = queryClient.getQueryData(PROJECT_CLIENT_TICKETS_QUERY_KEY(projectId, { page: pageParam - 1 }));
            const prevList = Array.isArray(prevData?.items) ? prevData.items : [];
            
            if (prevList.length > 0) {
                const res = await getProjectClientTickets(projectId, { page: pageParam, limit: 100 });
                return {
                    items: [...prevList, ...(res?.items ?? [])],
                    total: res?.total ?? 0,
                };
            } else {
                const promises = [];
                for (let p = 1; p <= pageParam; p++) {
                    promises.push(getProjectClientTickets(projectId, { page: p, limit: 100 }));
                }
                const results = await Promise.all(promises);
                const items = [];
                let total = 0;
                results.forEach((res) => {
                    items.push(...(res?.items ?? []));
                    if (res?.total != null) total = res.total;
                });
                return { items, total };
            }
        },
        enabled: !!projectId && canView && !!isClientTicketsEnabled,
        placeholderData: keepPreviousData,
    });

    const rows = Array.isArray(query.data?.items) ? query.data.items : [];
    const partitioned = useMemo(() => partitionClientTicketRows(rows), [rows]);
    const submittedRowsForBoard = useMemo(
        () =>
            mergeSubmittedToClientIntoSubmitted
                ? [...partitioned.submitted, ...partitioned.submittedToClient]
                : partitioned.submitted,
        [mergeSubmittedToClientIntoSubmitted, partitioned.submitted, partitioned.submittedToClient],
    );
    const activeRowsForBoard = useMemo(
        () =>
            mergeSubmittedIntoActive
                ? [...partitioned.active, ...partitioned.submitted]
                : partitioned.active,
        [mergeSubmittedIntoActive, partitioned.active, partitioned.submitted],
    );

    const createMutation = useMutation({
        mutationFn: (body) => createProjectClientTicket(projectId, body),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });
    const updateMutation = useMutation({
        mutationFn: ({ id, body }) => updateProjectClientTicket(projectId, id, body),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });
    const acknowledgeMutation = useMutation({
        mutationFn: (id) => acknowledgeProjectClientTicket(projectId, id),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });
    const submitMutation = useMutation({
        mutationFn: ({ id, body }) => submitProjectClientTicket(projectId, id, body),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });
    const submitToClientMutation = useMutation({
        mutationFn: (id) => submitProjectClientTicketToClient(projectId, id),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });

    const createModal = useProjectClientTicketCreateModal({
        canCreate,
        mentionUsers,
        createClientTicket: (payload, callbacks = {}) => {
            createMutation.mutate(payload, {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            });
        },
        createPending: createMutation.isPending,
    });

    const drag = useProjectClientTicketKanbanDrag({
        canSubmit,
        canSubmitToClient,
        canUpdate,
        canAcknowledge,
        allowSubmittedColumnTransitions: mergeSubmittedToClientIntoSubmitted,
        onMove: ({ id, targetColumn, callbacks }) => {
            if (targetColumn === CLIENT_TICKET_COL.ACTIVE) {
                updateMutation.mutate(
                    { id, body: { status: CLIENT_TICKET_COL.ACTIVE } },
                    {
                        onSuccess: callbacks?.onSuccess,
                        onError: callbacks?.onError,
                    },
                );
                return;
            }
            if (targetColumn === CLIENT_TICKET_COL.SUBMITTED) {
                submitMutation.mutate({ id }, {
                    onSuccess: callbacks?.onSuccess,
                    onError: callbacks?.onError,
                });
                return;
            }
            if (targetColumn === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT) {
                submitToClientMutation.mutate(id, {
                    onSuccess: callbacks?.onSuccess,
                    onError: callbacks?.onError,
                });
                return;
            }
            if (targetColumn === CLIENT_TICKET_COL.ACKNOWLEDGED) {
                acknowledgeMutation.mutate(id, {
                    onSuccess: callbacks?.onSuccess,
                    onError: callbacks?.onError,
                });
            }
        },
    });

    const moveToSubmitted = useCallback(
        (item, submitBody = {}, callbacks = {}) => {
            const id = getClientTicketApiId(item);
            if (!id || !canSubmit) return;
            let payload = submitBody;
            if (submitBody?.files?.length > 0) {
                payload = new FormData();
                payload.append('state', submitBody.state || 'genuine');
                if (submitBody.reason) {
                    payload.append('reason', submitBody.reason);
                }
                if (submitBody.attachments?.length > 0) {
                    payload.append('attachmentUrls', JSON.stringify(submitBody.attachments));
                }
                submitBody.files.forEach((f) => payload.append('files', f));
            } else {
                payload = {
                    state: submitBody.state || 'genuine',
                    ...(submitBody.reason ? { reason: submitBody.reason } : {}),
                    ...(submitBody.attachments?.length > 0 ? { attachments: submitBody.attachments } : {}),
                };
            }
            submitMutation.mutate({ id, body: payload }, {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            });
        },
        [canSubmit, submitMutation],
    );

    const moveToSubmittedToClient = useCallback(
        (item, callbacks = {}) => {
            const id = getClientTicketApiId(item);
            if (!id || !canSubmitToClient) return;
            submitToClientMutation.mutate(id, {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            });
        },
        [canSubmitToClient, submitToClientMutation],
    );

    const acknowledgeTicket = useCallback(
        (item, callbacks = {}) => {
            const id = getClientTicketApiId(item);
            if (!id || !canAcknowledge) return;
            acknowledgeMutation.mutate(id, {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            });
        },
        [canAcknowledge, acknowledgeMutation],
    );

    return {
        activeRows: activeRowsForBoard,
        submittedRows: submittedRowsForBoard,
        submittedToClientRows: partitioned.submittedToClient,
        acknowledgedRows: partitioned.acknowledged,
        total: query.data?.total ?? rows.length,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        canView,
        canCreate,
        canUpdate,
        canSubmit,
        canSubmitToClient,
        canAcknowledge,
        canDrag: mergeSubmittedToClientIntoSubmitted
            ? canUpdate || canAcknowledge
            : canSubmit || canSubmitToClient,
        createModal,
        movePending:
            updateMutation.isPending ||
            submitMutation.isPending ||
            submitToClientMutation.isPending,
        acknowledgePending: acknowledgeMutation.isPending,
        moveToSubmitted,
        moveToSubmittedToClient,
        acknowledgeTicket,
        ...drag,
        refetch: query.refetch,
    };
}

function useProjectClientTicketCreateModal({
    canCreate,
    mentionUsers,
    createClientTicket,
    createPending,
}) {
    const [form] = Form.useForm();
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [pendingLinks, setPendingLinks] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalForm] = Form.useForm();
    const linkModalCallbackRef = useRef(null);

    const openModal = useCallback(() => {
        if (!canCreate) return;
        form.resetFields();
        setModalOpen(true);
    }, [canCreate, form]);

    const closeModal = useCallback(() => {
        if (createPending) return;
        setModalOpen(false);
        setPendingAttachments((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return [];
        });
        setPendingLinks([]);
        setLinkModalOpen(false);
        linkModalCallbackRef.current = null;
        form.resetFields();
        linkModalForm.resetFields();
    }, [createPending, form, linkModalForm]);

    const handleFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({
            id: `client-ticket-file-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
            preview: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        setPendingAttachments((prev) => [...prev, ...next]);
    }, []);

    const removeAttachment = useCallback(
        (id) => {
            setPendingAttachments((prev) => {
                const index = prev.findIndex((p) => p.id === id);
                if (index < 0) return prev.filter((p) => p.id !== id);
                const target = prev[index];
                if (target?.preview) {
                    try {
                        URL.revokeObjectURL(target.preview);
                    } catch {
                        // ignore
                    }
                }
                let content = form.getFieldValue('description') || '';
                const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
                content = content.replace(linkRegex, '');
                for (let j = prev.length - 1; j > index; j--) {
                    content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                }
                form.setFieldValue('description', content);
                return prev.filter((p) => p.id !== id);
            });
        },
        [form],
    );

    const handleLinkRequest = useCallback(
        (insertLink) => {
            linkModalCallbackRef.current = insertLink;
            linkModalForm.resetFields();
            setLinkModalOpen(true);
        },
        [linkModalForm],
    );

    const handleLinkModalSubmit = useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const rawUrl = (values.url || '').trim();
            const title = (values.title || '').trim();
            const check = validateLinkUrlInput(rawUrl);
            if (!check.ok) {
                linkModalForm.setFields([
                    { name: 'url', errors: [check.message || 'Enter a valid URL (https://example.com)'] },
                ]);
                return;
            }
            const normalizedUrl = check.normalized;
            const displayTitle = title || normalizedUrl;
            const insertLink = linkModalCallbackRef.current;
            if (insertLink) insertLink({ url: normalizedUrl, title: displayTitle });
            setPendingLinks((prev) => [
                ...prev,
                { id: `client-ticket-link-${Date.now()}`, url: normalizedUrl, title: displayTitle },
            ]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkModalForm]);

    const handleSubmit = useCallback(() => {
        form.validateFields().then((values) => {
            const files = pendingAttachments.map((p) => p.file).filter(Boolean);
            const payload = buildCreateClientTicketPayload(values, files, pendingLinks);
            if (!payload) {
                message.warning('Please complete required fields');
                return;
            }
            createClientTicket(payload, {
                onSuccess: () => {
                    message.success('Ticket created successfully');
                    closeModal();
                },
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to create ticket'),
            });
        });
    }, [form, pendingAttachments, pendingLinks, createClientTicket, closeModal]);

    return {
        canCreate,
        mentionUsers,
        form,
        modalOpen,
        openModal,
        closeModal,
        handleSubmit,
        createPending,
        pendingAttachments,
        pendingLinks,
        handleFilesSelected,
        removeAttachment,
        linkModalOpen,
        setLinkModalOpen,
        linkModalForm,
        linkModalCallbackRef,
        handleLinkRequest,
        handleLinkModalSubmit,
    };
}

function useProjectClientTicketKanbanDrag({
    canSubmit,
    canSubmitToClient,
    canUpdate,
    canAcknowledge,
    allowSubmittedColumnTransitions = false,
    onMove,
}) {
    const [dropTarget, setDropTarget] = useState(null);
    const [draggingColumn, setDraggingColumn] = useState(null);

    const clearDropTarget = useCallback(() => {
        setDropTarget(null);
        setDraggingColumn(null);
    }, []);

    const handleDndDragStart = useCallback((payload) => {
        setDraggingColumn(payload?.sourceColumn ?? null);
    }, []);

    const handleDndDragOverColumn = useCallback((columnId) => {
        setDropTarget(columnId);
    }, []);

    const canMoveToColumn = useCallback(
        (source, target) => {
            if (allowSubmittedColumnTransitions) {
                if (source === CLIENT_TICKET_COL.SUBMITTED && target === CLIENT_TICKET_COL.ACTIVE) {
                    return canUpdate;
                }
                if (source === CLIENT_TICKET_COL.SUBMITTED && target === CLIENT_TICKET_COL.ACKNOWLEDGED) {
                    return canAcknowledge;
                }
            }
            if (source === CLIENT_TICKET_COL.ACTIVE && target === CLIENT_TICKET_COL.SUBMITTED) return canSubmit;
            if (source === CLIENT_TICKET_COL.ACTIVE && target === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT) {
                return canSubmitToClient;
            }
            if (source === CLIENT_TICKET_COL.SUBMITTED && target === CLIENT_TICKET_COL.SUBMITTED_TO_CLIENT) {
                return canSubmitToClient;
            }
            return false;
        },
        [allowSubmittedColumnTransitions, canUpdate, canAcknowledge, canSubmit, canSubmitToClient],
    );

    const handleDndDragEnd = useCallback(
        ({ overColumnId, payload }) => {
            const id = payload?.id;
            const sourceColumn = payload?.sourceColumn ?? null;
            const targetColumn = overColumnId ?? null;
            clearDropTarget();
            if (!id || !sourceColumn || !targetColumn || sourceColumn === targetColumn) return;
            if (!canMoveToColumn(sourceColumn, targetColumn)) {
                message.warning('That move is not allowed for your permissions or this board.');
                return;
            }
            onMove?.({
                id,
                sourceColumn,
                targetColumn,
                callbacks: {
                    onSuccess: () => message.success('Status updated'),
                    onError: (err) =>
                        message.error(err?.response?.data?.message || err?.message || 'Failed to update status'),
                },
            });
        },
        [clearDropTarget, canMoveToColumn, onMove],
    );

    return {
        dropTarget,
        draggingColumn,
        clearDropTarget,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
    };
}

export function getClientTicketDisplayId(item) {
    const raw =
        item?.clientTicketNumber ??
        item?.client_ticket_number ??
        item?.number ??
        item?.code ??
        getClientTicketId(item);
    if (raw == null || raw === '') return 'CT-—';
    const s = String(raw).trim();
    if (!s) return 'CT-—';
    if (/^CT-/i.test(s)) return s;
    return `CT-${s}`;
}

export function getClientTicketMarkdown(item) {
    const d = item?.description;
    const mentions = item?.descriptionMentions;
    if (d == null) return '';
    if (typeof d === 'string') return resolveDescriptionMentionsForDisplay(d.trim(), mentions);
    if (typeof d?.markdown === 'string') return resolveDescriptionMentionsForDisplay(d.markdown.trim(), mentions);
    if (typeof d?.content === 'string') return resolveDescriptionMentionsForDisplay(d.content.trim(), mentions);
    return resolveDescriptionMentionsForDisplay('', mentions) || '';
}

export function getClientTicketApiId(item) {
    const id = getClientTicketId(item);
    return id == null || id === '' ? undefined : String(id);
}
